import Immutable from 'immutable'
import moment from 'moment'

import type { Branded } from '@library/type-utils'

import { actionTypes } from 'actions/actionTypes'
import type { CoreSlotId, OrderId, TariffId } from 'actions/menu'
import basketConfig from 'config/basket'
import type { PortionSizes } from 'config/portionSizes'
import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { type BasketRecipe, normaliseBasketRecipe } from 'utils/basket'
import type { ImmutableList, ImmutableMap } from 'utils/types/immutable'

export type BasketAddress = Branded<
  ImmutableMap<{
    county: string
    id: string
    line1: string
    line2: string
    line3: string
    name: string
    postcode: string
    shippingDefault: boolean
    state: string
    town: string
  }>,
  'BasketAddress'
>
export type BasketEditBox = Branded<unknown, 'BasketEditBox'>
export type BasketOrderDetails = Branded<
  ImmutableMap<{
    menuId?: string
    menuUuid?: string
    period: ImmutableMap<{
      id: string
    }>
    recipeItems: ImmutableList<
      ImmutableMap<{
        recipeUuid: RecipeUUID
      }>
    >
    shippingAddress: ImmutableMap<{
      postcode: string
    }>
  }>,
  'BasketOrderDetails'
>
export type BasketQuantities = ImmutableMap<Record<string, number>>
export type RecipeQuantities = ImmutableMap<Record<RecipeUUID, BasketRecipe>>
export type RecipePositions = ImmutableList<RecipeUUID>

export type SourceBasketState = {
  address: BasketAddress | null
  addressTypeEdited: boolean
  boxId: string | null
  chosenAddress: BasketAddress | null
  currentMenuId: string
  currentMenuUuid: string
  date: string
  editBox?: BasketEditBox
  gifts: BasketQuantities
  hasAddedFirstRecipe: boolean
  isFlexOrder: boolean
  isPortionSizeViewVisible: boolean
  isShowDetailsViewFirst: boolean
  limitReached: boolean
  numPortions: PortionSizes
  orderDetails?: BasketOrderDetails
  orderId: OrderId | '' | undefined
  postcode: string
  postcodePending?: boolean
  prevAddress: BasketAddress | null
  prevDate: string
  prevPostcode: string
  prevSlotId: CoreSlotId | ''
  previewOrderId: OrderId | ''
  products: BasketQuantities
  promoCode: string
  promoCodeApplied: boolean
  promoCodeUrl: string
  recipePositions: RecipePositions
  recipes: RecipeQuantities
  slotId: CoreSlotId | ''
  tariffId: TariffId | null
  unsaved: boolean
}

export type BasketState = ImmutableMap<SourceBasketState>

type ReducerAction<Type extends string, Payload extends object = Record<never, never>> = Payload & {
  type: Type
}

type BasketAction =
  | ReducerAction<typeof actionTypes.BASKET_ADDRESS_CHANGE, { address: BasketAddress | null }>
  | ReducerAction<
      typeof actionTypes.BASKET_CHOSEN_ADDRESS_CHANGE,
      { address: BasketAddress | null }
    >
  | ReducerAction<
      typeof actionTypes.BASKET_RESET,
      { payload?: { chosenAddress?: BasketAddress | null } }
    >
  | ReducerAction<typeof actionTypes.BASKET_CHECKOUT>
  | ReducerAction<typeof actionTypes.BASKET_DATE_CHANGE, { date: string }>
  | ReducerAction<
      typeof actionTypes.BASKET_DELIVERYDAY_CHANGE,
      { date: string; slotId: CoreSlotId | '' }
    >
  | ReducerAction<typeof actionTypes.BASKET_GIFT_ADD, { giftId: string }>
  | ReducerAction<typeof actionTypes.BASKET_ID_CHANGE, { orderId: OrderId | undefined }>
  | ReducerAction<typeof actionTypes.BASKET_IS_FLEX_ORDER, { isFlexOrder: boolean }>
  | ReducerAction<typeof actionTypes.BASKET_IS_PORTION_SIZE_VIEW_VISIBLE, { value: boolean }>
  | ReducerAction<typeof actionTypes.BASKET_IS_SHOW_DETAILS_VIEW_FIRST, { value: boolean }>
  | ReducerAction<typeof actionTypes.BASKET_LIMIT_REACHED, { limitReached: boolean }>
  | ReducerAction<typeof actionTypes.BASKET_NUM_PORTION_CHANGE, { numPortions: string | number }>
  | ReducerAction<
      typeof actionTypes.BASKET_ORDER_DETAILS_LOADED,
      { orderDetails: BasketOrderDetails; orderId?: OrderId }
    >
  | ReducerAction<typeof actionTypes.BASKET_ORDER_LOADED, { editBox: BasketEditBox }>
  | ReducerAction<
      typeof actionTypes.BASKET_POSTCODE_CHANGE,
      { forgetPrevPostcode?: boolean; postcode: string }
    >
  | ReducerAction<typeof actionTypes.BASKET_POSTCODE_PENDING, { pending: boolean }>
  | ReducerAction<
      typeof actionTypes.BASKET_PREVIEW_ORDER_CHANGE,
      {
        boxId: string | null
        previewOrderId: OrderId | ''
      }
    >
  | ReducerAction<typeof actionTypes.BASKET_PRODUCT_ADD, { productId: string; unsaved?: boolean }>
  | ReducerAction<
      typeof actionTypes.BASKET_PRODUCT_REMOVE,
      { productId: string; unsaved?: boolean }
    >
  | ReducerAction<
      typeof actionTypes.BASKET_PROMO_CODE_APPLIED_CHANGE,
      { promoCodeApplied: boolean }
    >
  | ReducerAction<typeof actionTypes.BASKET_PROMO_CODE_CHANGE, { promoCode: string }>
  | ReducerAction<typeof actionTypes.BASKET_PROMO_CODE_URL_CHANGE, { promoCodeUrl: string }>
  | ReducerAction<
      typeof actionTypes.BASKET_RECIPES_INITIALISE,
      { recipes: Record<RecipeUUID, BasketRecipe | LegacyBasketRecipe> }
    >
  | ReducerAction<
      typeof actionTypes.BASKET_RECIPE_ADD,
      {
        isOrderRecipe?: boolean
        numPortions?: PortionSizes
        recipeId: RecipeUUID
      }
    >
  | ReducerAction<
      typeof actionTypes.BASKET_RECIPE_PORTION_CHANGE,
      {
        numPortions: number
        recipeId: RecipeUUID
      }
    >
  | ReducerAction<
      typeof actionTypes.BASKET_RECIPE_POSITIONS_INITIALISE,
      { recipePositions: RecipeUUID[] }
    >
  | ReducerAction<
      typeof actionTypes.BASKET_RECIPE_REMOVE,
      {
        markUnsaved?: boolean
        recipeId: RecipeUUID
      }
    >
  | ReducerAction<typeof actionTypes.BASKET_SLOT_CHANGE, { slotId: CoreSlotId | '' }>
  | ReducerAction<typeof actionTypes.BASKET_TARIFF_CHANGE, { tariffId: TariffId | null }>
  | ReducerAction<
      typeof actionTypes.MENU_SERVICE_V3_DATA_RECEIVED,
      { response: { core_id: string; id: string } }
    >
  | ReducerAction<typeof actionTypes.SET_BASKET_PRODUCTS, { products: Record<string, number> }>
  | ReducerAction<typeof actionTypes.USER_LOGGED_IN>

type BasketActionFor<Type extends BasketAction['type']> = Extract<BasketAction, { type: Type }>

type UnknownBasketAction = {
  [key: string]: unknown
  type?: string
}

const emptyRecipePositions = () => Immutable.List<RecipeUUID>() as unknown as RecipePositions

type LegacyBasketRecipe = number
type BasketRecipeInput = BasketRecipe | LegacyBasketRecipe | undefined

const toBasketRecipe = (entry: BasketRecipeInput, numPortions: PortionSizes): BasketRecipe =>
  normaliseBasketRecipe(entry, numPortions)

const initialState = (): BasketState =>
  Immutable.fromJS({
    address: null,
    addressTypeEdited: false,
    boxId: null,
    chosenAddress: null,
    currentMenuId: '',
    currentMenuUuid: '',
    date: '',
    gifts: {},
    isPortionSizeViewVisible: false,
    isShowDetailsViewFirst: true,
    limitReached: false,
    numPortions: 2,
    orderId: '',
    postcode: '',
    prevDate: '',
    prevPostcode: '',
    prevSlotId: '',
    prevAddress: null,
    products: {},
    promoCode: '',
    promoCodeApplied: false,
    promoCodeUrl: '',
    recipes: Immutable.Map(),
    hasAddedFirstRecipe: false,
    slotId: '',
    unsaved: false,
    previewOrderId: '',
    tariffId: null,
    recipePositions: Immutable.List(),
    isFlexOrder: false,
  }) as unknown as BasketState

const typed = <T extends BasketAction['type']>(
  action: BasketAction | UnknownBasketAction,
): BasketActionFor<T> => action as BasketActionFor<T>

const basket = {
  basket: (
    state: BasketState | undefined,
    action: BasketAction | UnknownBasketAction,
  ): BasketState => {
    if (!state) {
      // TODO delivery_day_id
      return initialState()
    }

    switch (action.type) {
      case actionTypes.BASKET_ADDRESS_CHANGE: {
        let newState = state
        const prevAddress = state.get('address')
        newState = newState.set('prevAddress', prevAddress)

        return newState.set(
          'address',
          typed<typeof actionTypes.BASKET_ADDRESS_CHANGE>(action).address,
        )
      }

      case actionTypes.BASKET_CHECKOUT: {
        return state.set('unsaved', false)
      }

      case actionTypes.BASKET_DATE_CHANGE: {
        let newState = state
        const date = moment(typed<typeof actionTypes.BASKET_DATE_CHANGE>(action).date)
        if (state.get('date')) {
          newState = newState.set('prevDate', state.get('date'))
        }
        newState = newState.set('date', date.format('YYYY-MM-DD'))

        return newState
      }

      case actionTypes.MENU_SERVICE_V3_DATA_RECEIVED: {
        const { response } = typed<typeof actionTypes.MENU_SERVICE_V3_DATA_RECEIVED>(action)

        return state.set('currentMenuId', response.core_id).set('currentMenuUuid', response.id)
      }

      case actionTypes.BASKET_GIFT_ADD: {
        const { giftId } = typed<typeof actionTypes.BASKET_GIFT_ADD>(action)
        const currentQty = state.getIn(['gifts', giftId] as const, 0)

        return state.setIn(['gifts', giftId] as const, currentQty + 1)
      }

      case actionTypes.BASKET_IS_PORTION_SIZE_VIEW_VISIBLE: {
        return state.set(
          'isPortionSizeViewVisible',
          typed<typeof actionTypes.BASKET_IS_PORTION_SIZE_VIEW_VISIBLE>(action).value,
        )
      }

      case actionTypes.BASKET_IS_SHOW_DETAILS_VIEW_FIRST: {
        return state.set(
          'isShowDetailsViewFirst',
          typed<typeof actionTypes.BASKET_IS_SHOW_DETAILS_VIEW_FIRST>(action).value,
        )
      }

      case actionTypes.BASKET_ID_CHANGE: {
        return state.set('orderId', typed<typeof actionTypes.BASKET_ID_CHANGE>(action).orderId)
      }

      case actionTypes.BASKET_ORDER_LOADED: {
        return state.set('editBox', typed<typeof actionTypes.BASKET_ORDER_LOADED>(action).editBox)
      }
      case actionTypes.BASKET_ORDER_DETAILS_LOADED: {
        const { orderDetails, orderId } =
          typed<typeof actionTypes.BASKET_ORDER_DETAILS_LOADED>(action)
        const currentOrderId = state.get('orderId')
        const isSameOrder =
          !!orderId && !!currentOrderId && String(orderId) === String(currentOrderId)

        if (!isSameOrder) {
          return state.set('orderDetails', orderDetails)
        }

        const existingOrderDetails = state.get('orderDetails')
        const menuId = orderDetails.get('menuId') || existingOrderDetails?.get('menuId')
        const menuUuid = orderDetails.get('menuUuid') || existingOrderDetails?.get('menuUuid')
        let nextState = state.set('orderDetails', orderDetails)

        if (menuId) {
          nextState = nextState.setIn(['orderDetails', 'menuId'], menuId)
        }

        if (menuUuid) {
          nextState = nextState.setIn(['orderDetails', 'menuUuid'], menuUuid)
        }

        return nextState
      }

      case actionTypes.BASKET_NUM_PORTION_CHANGE: {
        let portionSize = parseInt(
          typed<typeof actionTypes.BASKET_NUM_PORTION_CHANGE>(action).numPortions as string,
          10,
        )

        if (basketConfig.portions.allowed.indexOf(portionSize) === -1) {
          portionSize = basketConfig.portions.default
        }

        const numPortions = portionSize as PortionSizes
        const recipes = Object.fromEntries(
          Object.entries(state.get('recipes').toJS()).map(([recipeId, entry]) => {
            const currentRecipe = normaliseBasketRecipe(
              entry as BasketRecipe | number | undefined,
              numPortions,
            )

            return [
              recipeId,
              {
                quantity: currentRecipe.quantity,
                numPortions,
              },
            ]
          }),
        )

        return state
          .set('numPortions', numPortions)
          .set('recipes', Immutable.fromJS(recipes) as unknown as RecipeQuantities)
      }

      case actionTypes.BASKET_POSTCODE_CHANGE: {
        const { forgetPrevPostcode, postcode } =
          typed<typeof actionTypes.BASKET_POSTCODE_CHANGE>(action)
        let newState = state
        if (state.get('postcode')) {
          newState = newState.set(
            'prevPostcode',
            !forgetPrevPostcode ? state.get('postcode') : postcode,
          )
        }
        newState = newState.set('postcode', postcode)

        return newState
      }
      case actionTypes.BASKET_POSTCODE_PENDING: {
        return state.set(
          'postcodePending',
          typed<typeof actionTypes.BASKET_POSTCODE_PENDING>(action).pending,
        )
      }

      case actionTypes.SET_BASKET_PRODUCTS: {
        return state.setIn(
          ['products'] as const,
          Immutable.fromJS(
            typed<typeof actionTypes.SET_BASKET_PRODUCTS>(action).products,
          ) as unknown as BasketQuantities,
        )
      }

      case actionTypes.BASKET_PRODUCT_ADD: {
        const { productId, unsaved } = typed<typeof actionTypes.BASKET_PRODUCT_ADD>(action)
        const currentQty = state.getIn(['products', productId] as const, 0)

        let newState = state.setIn(['products', productId] as const, currentQty + 1)

        if (unsaved) {
          newState = newState.set('unsaved', true)
        }

        return newState
      }

      case actionTypes.BASKET_PRODUCT_REMOVE: {
        const { productId, unsaved } = typed<typeof actionTypes.BASKET_PRODUCT_REMOVE>(action)
        const currentQty = state.getIn(['products', productId] as const, 0)
        let newState = state

        if (unsaved) {
          newState = newState.set('unsaved', true)
        }

        if (currentQty > 1) {
          newState = newState.setIn(['products', productId] as const, currentQty - 1)
        } else {
          newState = newState.deleteIn(['products', productId] as const)
        }

        return newState
      }

      case actionTypes.BASKET_PROMO_CODE_CHANGE: {
        const { promoCode } = typed<typeof actionTypes.BASKET_PROMO_CODE_CHANGE>(action)

        return state.set(
          'promoCode',
          typeof promoCode === 'string' ? promoCode.toUpperCase() : promoCode,
        )
      }

      case actionTypes.BASKET_PROMO_CODE_APPLIED_CHANGE: {
        return state.set(
          'promoCodeApplied',
          typed<typeof actionTypes.BASKET_PROMO_CODE_APPLIED_CHANGE>(action).promoCodeApplied,
        )
      }

      case actionTypes.BASKET_PROMO_CODE_URL_CHANGE: {
        return state.set(
          'promoCodeUrl',
          typed<typeof actionTypes.BASKET_PROMO_CODE_URL_CHANGE>(action).promoCodeUrl,
        )
      }

      case actionTypes.BASKET_RECIPES_INITIALISE: {
        const { recipes } = typed<typeof actionTypes.BASKET_RECIPES_INITIALISE>(action)
        const numPortions = state.get('numPortions')
        const recipeEntries = Object.fromEntries(
          Object.entries(recipes).map(([recipeId, entry]) => [
            recipeId,
            toBasketRecipe(entry, numPortions),
          ]),
        )

        return state.set('recipes', Immutable.fromJS(recipeEntries) as unknown as RecipeQuantities)
      }

      case actionTypes.BASKET_RECIPE_POSITIONS_INITIALISE: {
        const { recipePositions } =
          typed<typeof actionTypes.BASKET_RECIPE_POSITIONS_INITIALISE>(action)

        return state.set(
          'recipePositions',
          Immutable.List(recipePositions) as unknown as RecipePositions,
        )
      }

      case actionTypes.BASKET_RECIPE_ADD: {
        const {
          recipeId,
          isOrderRecipe,
          numPortions: recipeNumPortions,
        } = typed<typeof actionTypes.BASKET_RECIPE_ADD>(action)
        const currentRecipe = state.getIn(['recipes', recipeId] as const) as
          | BasketRecipe
          | number
          | undefined
        const numPortions = recipeNumPortions ?? state.get('numPortions')
        const currentQty = normaliseBasketRecipe(currentRecipe, numPortions).quantity
        const hasAddedFirstRecipe = state.get('hasAddedFirstRecipe')
        const recipePositions = state.get('recipePositions', emptyRecipePositions())

        let newState = state.setIn(
          ['recipes', recipeId] as const,
          Immutable.fromJS({
            quantity: currentQty + 1,
            numPortions,
          }),
        )

        if (!hasAddedFirstRecipe) {
          newState = newState.setIn(['hasAddedFirstRecipe'] as const, true)
        }

        const newRecipePositions = recipePositions.push(recipeId)

        newState = newState.set('recipePositions', newRecipePositions)

        if (state.get('orderId') && !isOrderRecipe) {
          newState = newState.set('unsaved', true)
        }

        return newState
      }

      case actionTypes.BASKET_RECIPE_PORTION_CHANGE: {
        const { recipeId, numPortions } =
          typed<typeof actionTypes.BASKET_RECIPE_PORTION_CHANGE>(action)
        const requestedNumPortions = Number(numPortions)

        if (!basketConfig.portions.allowed.includes(requestedNumPortions)) {
          return state
        }

        const currentRecipe = state.getIn(['recipes', recipeId] as const) as
          | BasketRecipe
          | number
          | undefined

        if (currentRecipe === undefined) {
          return state
        }

        const currentBasketRecipe = normaliseBasketRecipe(currentRecipe, state.get('numPortions'))

        let newState = state.setIn(
          ['recipes', recipeId] as const,
          Immutable.fromJS({
            quantity: currentBasketRecipe.quantity,
            numPortions: requestedNumPortions as PortionSizes,
          }),
        )

        if (state.get('orderId')) {
          newState = newState.set('unsaved', true)
        }

        return newState
      }

      case actionTypes.BASKET_LIMIT_REACHED: {
        return state.set(
          'limitReached',
          typed<typeof actionTypes.BASKET_LIMIT_REACHED>(action).limitReached,
        )
      }

      case actionTypes.BASKET_RECIPE_REMOVE: {
        const { recipeId, markUnsaved } = typed<typeof actionTypes.BASKET_RECIPE_REMOVE>(action)
        const currentRecipe = state.getIn(['recipes', recipeId] as const) as
          | BasketRecipe
          | number
          | undefined
        const currentBasketRecipe = normaliseBasketRecipe(currentRecipe, state.get('numPortions'))
        const currentQty = currentBasketRecipe.quantity
        const recipePositions = state.get('recipePositions', emptyRecipePositions())
        let newState = state
        if (currentQty === 1) {
          newState = state.deleteIn(['recipes', recipeId] as const, 0)
        } else if (currentQty > 0) {
          newState = state.setIn(
            ['recipes', recipeId] as const,
            Immutable.fromJS({
              quantity: currentQty - 1,
              numPortions: currentBasketRecipe.numPortions,
            }),
          )
        }

        const recipePositionToRemove = recipePositions.lastIndexOf(recipeId)
        if (recipePositionToRemove !== -1) {
          newState = newState.set('recipePositions', recipePositions.delete(recipePositionToRemove))
        }

        if (state.get('orderId') && markUnsaved !== false) {
          newState = newState.set('unsaved', true)
        }

        return newState
      }

      case actionTypes.BASKET_DELIVERYDAY_CHANGE: {
        const { date, slotId } = typed<typeof actionTypes.BASKET_DELIVERYDAY_CHANGE>(action)
        let newState = state
        newState = newState.set('date', date)
        newState = newState.set('slotId', slotId)

        return newState
      }

      case actionTypes.BASKET_SLOT_CHANGE: {
        let newState = state
        if (state.get('slotId')) {
          newState = newState.set('prevSlotId', state.get('slotId'))
        }
        newState = newState.set(
          'slotId',
          typed<typeof actionTypes.BASKET_SLOT_CHANGE>(action).slotId,
        )

        return newState
      }

      case actionTypes.BASKET_CHOSEN_ADDRESS_CHANGE: {
        return state.set(
          'chosenAddress',
          Immutable.fromJS(
            typed<typeof actionTypes.BASKET_CHOSEN_ADDRESS_CHANGE>(action).address,
          ) as unknown as BasketAddress,
        )
      }

      case actionTypes.BASKET_PREVIEW_ORDER_CHANGE: {
        const { previewOrderId, boxId } =
          typed<typeof actionTypes.BASKET_PREVIEW_ORDER_CHANGE>(action)

        return state.merge({
          previewOrderId: previewOrderId,
          boxId: boxId,
        })
      }

      case actionTypes.USER_LOGGED_IN: {
        return state.set('tariffId', initialState().get('tariffId'))
      }

      case actionTypes.BASKET_TARIFF_CHANGE: {
        return state.set(
          'tariffId',
          typed<typeof actionTypes.BASKET_TARIFF_CHANGE>(action).tariffId,
        )
      }

      case actionTypes.BASKET_RESET: {
        const { payload } = typed<typeof actionTypes.BASKET_RESET>(action)
        let newState: BasketState = initialState()

        if (payload && payload.chosenAddress) {
          newState = newState.set(
            'chosenAddress',
            Immutable.fromJS(payload.chosenAddress) as unknown as BasketAddress,
          )
        }

        return newState
      }

      case actionTypes.BASKET_IS_FLEX_ORDER: {
        return state.set(
          'isFlexOrder',
          typed<typeof actionTypes.BASKET_IS_FLEX_ORDER>(action).isFlexOrder,
        )
      }

      default: {
        return state
      }
    }
  },
}

export { initialState }
// eslint-disable-next-line import/no-default-export
export default basket
